import { useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { motion } from "motion/react"
import {
    frameIsOnPaymentReturn,
    isPaymentDoneMessage,
    isPaymentReturnOrigin,
    performanceSawPaymentReturn,
} from "../../lib/cardPayment"

const BODY = "'Manrope', sans-serif"
const DISPLAY = "'Tilda Sans', sans-serif"
const SHEET_BG = "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)"
const RETURN_POLL_MS = 400
/** После load, если фокус не в iframe (не 3DS) — закрываем белую страницу банка */
const IDLE_RETURN_MS = 1600

type CardPaymentFrameProps = {
    url: string
    orderId?: string
    /** Банк вернул на blank.html / белая страница после оплаты — закрыть форму и проверять статус */
    onReturnDetected: () => void
    onClose?: () => void
    /** Вызывается на каждый load iframe — можно опросить статус заказа */
    onFrameNavigated?: () => void
    /** Встроить в текущий блок (pay overlay), без отдельной модалки */
    inline?: boolean
    title?: string
}

export default function CardPaymentFrame({
    url,
    orderId,
    onReturnDetected,
    onClose,
    onFrameNavigated,
    inline = false,
    title = "Оплата картой",
}: CardPaymentFrameProps) {
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const returnedRef = useRef(false)
    const loadCountRef = useRef(0)
    const idleTimerRef = useRef<number | null>(null)
    const onReturnRef = useRef(onReturnDetected)
    const onFrameNavigatedRef = useRef(onFrameNavigated)

    useEffect(() => {
        onReturnRef.current = onReturnDetected
    }, [onReturnDetected])

    useEffect(() => {
        onFrameNavigatedRef.current = onFrameNavigated
    }, [onFrameNavigated])

    const clearIdleTimer = () => {
        if (idleTimerRef.current != null) {
            window.clearTimeout(idleTimerRef.current)
            idleTimerRef.current = null
        }
    }

    const markReturned = useCallback(() => {
        if (returnedRef.current) return
        returnedRef.current = true
        clearIdleTimer()
        onReturnRef.current()
    }, [])

    const iframeHasFocus = () => document.activeElement === iframeRef.current

    const inspectFrame = useCallback(() => {
        if (frameIsOnPaymentReturn(iframeRef.current?.contentWindow) || performanceSawPaymentReturn()) {
            markReturned()
            return true
        }
        return false
    }, [markReturned])

    const scheduleIdleReturnCheck = useCallback(() => {
        clearIdleTimer()
        // Первая загрузка — форма банка, не трогаем. 3DS тоже load, но там обычно фокус в iframe.
        if (loadCountRef.current < 2) return

        idleTimerRef.current = window.setTimeout(() => {
            if (returnedRef.current) return
            if (inspectFrame()) return
            // Пользователь вводит код 3DS — не закрываем
            if (iframeHasFocus()) {
                scheduleIdleReturnCheck()
                return
            }
            // Белая/пустая страница после оплаты: фокуса нет, новых load нет
            markReturned()
        }, IDLE_RETURN_MS)
    }, [inspectFrame, markReturned])

    useEffect(() => {
        returnedRef.current = false
        loadCountRef.current = 0
        clearIdleTimer()

        const onMessage = (event: MessageEvent) => {
            if (!isPaymentReturnOrigin(event.origin)) return
            if (!isPaymentDoneMessage(event.data)) return
            if (orderId && event.data.orderId && event.data.orderId !== orderId) return
            markReturned()
        }

        window.addEventListener("message", onMessage)
        const pollId = window.setInterval(() => {
            inspectFrame()
        }, RETURN_POLL_MS)

        return () => {
            window.removeEventListener("message", onMessage)
            window.clearInterval(pollId)
            clearIdleTimer()
        }
    }, [inspectFrame, markReturned, orderId, url])

    const onIframeLoad = () => {
        loadCountRef.current += 1
        const hitReturn = inspectFrame()
        onFrameNavigatedRef.current?.()
        if (!hitReturn) {
            scheduleIdleReturnCheck()
        }
    }

    const frame = (
        <div className={`flex min-h-0 flex-col overflow-hidden ${inline ? "h-full min-h-0 flex-1" : "h-[min(720px,85svh)] w-full"}`}>
            <div
                className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 px-4 py-3"
                style={{ background: SHEET_BG }}>
                <p className="text-[15px] text-white" style={{ fontFamily: DISPLAY, fontWeight: 700 }}>
                    {title}
                </p>
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="cursor-pointer rounded-full px-3 py-1.5 text-[13px] text-white/60 transition hover:bg-white/10 hover:text-white"
                        style={{ fontFamily: BODY, fontWeight: 600 }}>
                        Закрыть
                    </button>
                )}
            </div>
            <iframe
                ref={iframeRef}
                src={url}
                title={title}
                onLoad={onIframeLoad}
                className="min-h-0 flex-1 w-full border-0 bg-white"
                allow="payment *; fullscreen"
            />
        </div>
    )

    if (inline) return frame

    if (typeof document === "undefined") return null

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[280] flex items-end justify-center md:items-center md:p-4"
            style={{ background: "rgba(10,18,42,0.85)", backdropFilter: "blur(8px)" }}>
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="flex h-[92svh] max-h-[92svh] w-full flex-col overflow-hidden rounded-t-[24px] md:h-[min(720px,90svh)] md:max-w-lg md:rounded-[24px]"
                style={{
                    background: SHEET_BG,
                    borderTop: "1.5px solid rgba(255,255,255,0.12)",
                }}>
                {frame}
            </motion.div>
        </motion.div>,
        document.body,
    )
}
